import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IDeviceStreamConfiguration } from "@formant/core/model/IDeviceStreamConfiguration";
import { IDeviceTeleopCustomStreamConfiguration } from "@formant/core/model/IDeviceTeleopCustomStreamConfiguration";
import { IDeviceTeleopHardwareStreamConfiguration } from "@formant/core/model/IDeviceTeleopHardwareStreamConfiguration";
import { IDeviceTeleopRosStreamConfiguration } from "@formant/core/model/IDeviceTeleopRosStreamConfiguration";
import { ITeleopJoystickConfiguration } from "@formant/core/model/ITeleopJoystickConfiguration";
import { IValidation } from "@formant/core/model/IValidation";
import {
    rosTypeBool,
    rosTypeFloat64,
    rosTypeJointState,
    rosTypePointStamped,
    rosTypePoseWithCovarianceStamped,
    rosTypeTwist,
} from "@formant/core/model/rosTypes";
import { defaultROSVersion } from "@formant/core/model/rosVersions";
import {
    rtcCompressedImageStreamType,
    rtcH264VideoFrameStreamType,
    rtcNumericStreamType,
    rtcPointStreamType,
    rtcTwistStreamType,
} from "@formant/core/model/rtcStreamTypes";
import { TeleopMode } from "@formant/core/model/TeleopMode";
import { Page } from "@formant/ui/component/common/Page";
import { VStack } from "@formant/ui/component/common/VStack";

import { NoDataMessage } from "../common/NoDataMessage";
import { Scrollable } from "../common/Scrollable";
import { TabWorkflowButtons } from "../common/TabWorkflowButtons";
import { ValidationMessages } from "../common/ValidationMessages";
import { Container } from "../Container";
import { ItemList } from "../ItemList";
import { IItem } from "../ItemList/IItem";
import { rosTopicsForDevices } from "../Observe/utils/rosTopicsForDevices";
import { AddButton } from "./AddButton";
import { EditStream } from "./EditStream";
import { TeleopAudioConfiguration } from "./TeleopAudioConfiguration";
import { TeleopButtonConfiguration } from "./TeleopButtonConfiguration";
import { TeleopCompressedImageConfiguration } from "./TeleopCompressedImageConfiguration";
import * as styles from "./TeleopControlsConfiguration.scss";
import { TeleopHardwareVideoConfiguration } from "./TeleopHardwareVideoConfiguration";
import { TeleopJointStateConfiguration } from "./TeleopJointStateConfiguration";
import { TeleopJoystickConfiguration } from "./TeleopJoystickConfiguration";
import { TeleopMouseClickConfiguration } from "./TeleopMouseClickConfiguration";
import { TeleopNumericConfiguration } from "./TeleopNumericConfiguration";
import { TeleopStreamConfigurationGroup } from "./TeleopStreamConfigurationGroup";

export interface ITeleopConfigurationProps {
    devices?: IDevice[];
    document: IDeviceConfigurationDocument;
    dirty: boolean;
    unsaved?: boolean;
    validation: IValidation;
    saveCopy?: string;
    onChange: (document: IDeviceConfigurationDocument) => void;
    onSave: () => void;
    loading: boolean;
    popup?: boolean;
}

@observer
export class TeleopControlsConfiguration extends Component<ITeleopConfigurationProps> {
    @observable
    private editingCompressedImage?: string;

    @observable
    private editingButton?: string;

    @observable
    private editingJointState?: string;

    @observable
    private editingMouseClick?: string;

    @observable
    private editingJoystick = false;

    @observable
    private editingJoystickVelocityStreamWithName = "";

    @observable
    private editingAudio?: string;

    @observable
    private editingHardwareVideo?: string;

    @observable
    private editingNumeric?: string;

    @observable
    private configuringMinimap = false;

    @observable
    private activeLocalizationStream?: IDeviceStreamConfiguration;

    public render() {
        const {
            devices,
            dirty,
            unsaved,
            document,
            validation,
            saveCopy,
            onSave,
            loading,
            popup,
        } = this.props;

        const joysticks = document.teleop?.joysticks || [];

        const joystickValidationKeys = Object.keys(validation).filter(_ =>
            _.includes("document.teleop.joysticks"),
        );

        const streamValidationKeys = Object.keys(validation).filter(_ =>
            _.includes("document.teleop.rosStreams"),
        );

        const customValidationKeys = Object.keys(validation).filter(_ =>
            _.includes("document.teleop.customStreams"),
        );

        const rosTopics = rosTopicsForDevices(devices);
        const floatTopics = rosTopics.filter(_ => _.type === rosTypeFloat64);

        const commonProps = {
            topics: rosTopics,
            document,
            onAddRosStream: this.onAddRosStream,
            onAddCustomStream: this.onAddCustomStream,
            onAddHardwareStream: this.onAddHardwareStream,
            onDeleteStream: this.onDeleteStream,
        };

        const localizationStreams = (document.telemetry?.streams || []).filter(
            _ => _.configuration.type === "ros-localization",
        );

        const localizationItems: IItem[] = localizationStreams.map(_ => {
            return {
                key: _.name,
                label: _.name,
                icon: "localization",
                checked: false,
                disabled: false,
                onEdit: this.onEditLocalizationStream,
                onDelete: this.onDeleteLocalizationStream,
            };
        });

        return (
            <Page
                className={classnames(
                    styles["teleop-configuration"],
                    popup && styles.popup,
                )}
            >
                <Scrollable>
                    <Container className={styles.container}>
                        <VStack innerSpacing="lg">
                            <>
                                <TeleopStreamConfigurationGroup
                                    label="Mouse Click"
                                    mode="command"
                                    customInputType="point"
                                    rosInputTypes={[
                                        "geometry_msgs/PointStamped",
                                    ]}
                                    itemName="controls"
                                    rosOutputTypes={[rosTypePointStamped]}
                                    specificOnEdit={this.onOpenMouseClickConfig}
                                    editableTypes={[
                                        rosTypePointStamped,
                                        rtcPointStreamType,
                                    ]}
                                    max={3}
                                    {...commonProps}
                                />
                            </>
                            <TeleopStreamConfigurationGroup
                                label={"Joystick sets"}
                                help="To use a gamepad or virtual joystick, first configure a Twist stream. Then click the edit button to finish setting up your joystick"
                                mode="command"
                                addStreamCopy={"Add joystick set"}
                                itemName="controls"
                                customInputType="twist"
                                rosInputTypes={[rosTypeTwist]}
                                rosOutputTypes={[rosTypeTwist]}
                                disableCustom={true}
                                specificOnEdit={this.onOpenJoystickConfig}
                                editableTypes={[
                                    rosTypeTwist,
                                    rtcTwistStreamType,
                                ]}
                                max={3}
                                {...commonProps}
                            />

                            <>
                                <TeleopStreamConfigurationGroup
                                    label="Joint Manipulator"
                                    mode="observe"
                                    itemName="controls"
                                    customInputType="joint-state"
                                    rosInputTypes={["sensor_msgs/JointState"]}
                                    rosOutputTypes={[rosTypeJointState]}
                                    specificOnEdit={this.onOpenJointStateConfig}
                                    disableCustom={false}
                                    editableTypes={[rosTypeJointState]}
                                    max={2}
                                    {...commonProps}
                                />

                                <TeleopStreamConfigurationGroup
                                    label="Relocalization"
                                    mode="command"
                                    customInputType="pose-with-covariance"
                                    rosInputTypes={[
                                        "geometry_msgs/PoseWithCovarianceStamped",
                                    ]}
                                    itemName="controls"
                                    rosOutputTypes={[
                                        rosTypePoseWithCovarianceStamped,
                                    ]}
                                    disableCustom={true}
                                    max={1}
                                    {...commonProps}
                                />
                                <TeleopStreamConfigurationGroup
                                    label="Buttons"
                                    help="Buttons publish boolean messages to the device. Useful for setting up real-time commands to your robot"
                                    mode="command"
                                    itemName="controls"
                                    customInputType="bitset"
                                    rosInputTypes={[rosTypeBool]}
                                    rosOutputTypes={[rosTypeBool]}
                                    specificOnEdit={this.onOpenButtonConfig}
                                    editableTypes={[rosTypeBool]}
                                    max={24}
                                    {...commonProps}
                                />
                                <TeleopStreamConfigurationGroup
                                    label="Status"
                                    help="Status subscribes to boolean messages from the device. Typically used to convey the state of your robot in real-time"
                                    mode="observe"
                                    itemName="controls"
                                    customInputType="bitset"
                                    rosInputTypes={[rosTypeBool]}
                                    rosOutputTypes={[rosTypeBool]}
                                    max={5}
                                    {...commonProps}
                                />

                                <div className={styles.localization}>
                                    <AddButton
                                        label="Navigation"
                                        className={styles.add}
                                        onClicked={this.onAddNewMinimapStart}
                                        count={localizationStreams.length}
                                        max={2}
                                    />
                                    {localizationItems.length > 0 ? (
                                        <ItemList
                                            className={styles.items}
                                            items={localizationItems}
                                        />
                                    ) : (
                                        <NoDataMessage asBanner={true}>
                                            No localization controls configured
                                        </NoDataMessage>
                                    )}

                                    {this.activeLocalizationStream && (
                                        <EditStream
                                            key={
                                                this.configuringMinimap ? 0 : 1
                                            }
                                            devices={devices}
                                            document={document}
                                            streams={[
                                                this.activeLocalizationStream,
                                            ]}
                                            editing={this.configuringMinimap}
                                            visible={this.configuringMinimap}
                                            onChange={this.props.onChange}
                                            onClose={this.onCloseMinimap}
                                            onAdd={this.onAddMinimap}
                                        />
                                    )}
                                </div>
                                <TeleopStreamConfigurationGroup
                                    label="Numeric slider"
                                    help=""
                                    mode="command"
                                    itemName="controls"
                                    customInputType="numeric"
                                    rosInputTypes={[rosTypeFloat64]}
                                    rosOutputTypes={[rosTypeFloat64]}
                                    max={16}
                                    specificOnEdit={this.onOpenNumericConfig}
                                    editableTypes={[
                                        rosTypeFloat64,
                                        rtcNumericStreamType,
                                    ]}
                                    {...commonProps}
                                />
                            </>

                            <ValidationMessages
                                validation={validation}
                                keys={[
                                    ...joystickValidationKeys,
                                    ...streamValidationKeys,
                                    ...customValidationKeys,
                                ]}
                            />
                            <TeleopCompressedImageConfiguration
                                visible={!!this.editingCompressedImage}
                                streamName={this.editingCompressedImage}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingCompressedImage}
                            />
                            <TeleopAudioConfiguration
                                visible={!!this.editingAudio}
                                streamName={this.editingAudio}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingAudio}
                            />
                            <TeleopButtonConfiguration
                                visible={!!this.editingButton}
                                streamName={this.editingButton}
                                topics={rosTopics}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingButtonConfig}
                            />
                            <TeleopMouseClickConfiguration
                                visible={!!this.editingMouseClick}
                                streamName={this.editingMouseClick}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingMouseClickConfig}
                            />
                            <TeleopJointStateConfiguration
                                visible={!!this.editingJointState}
                                streamName={this.editingJointState}
                                topics={rosTopics}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingJointStateConfig}
                            />
                            <TeleopJoystickConfiguration
                                visible={this.editingJoystick}
                                onClose={this.onCloseJoystickConfig}
                                editingVelocityStreamWithName={
                                    this.editingJoystickVelocityStreamWithName
                                }
                                joysticks={joysticks}
                                validation={validation}
                                onSave={this.onSaveJoystickConfig}
                                rosStreams={document.teleop?.rosStreams || []}
                                customStreams={
                                    document.teleop?.customStreams || []
                                }
                            />
                            <TeleopHardwareVideoConfiguration
                                visible={!!this.editingHardwareVideo}
                                streamName={this.editingHardwareVideo}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingHardwareVideoConfig}
                            />
                            <TeleopNumericConfiguration
                                visible={!!this.editingNumeric}
                                streamName={this.editingNumeric}
                                topics={floatTopics}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingNumericConfig}
                            />
                        </VStack>
                    </Container>
                </Scrollable>
                <TabWorkflowButtons
                    dirty={dirty}
                    unsaved={unsaved}
                    onSave={onSave}
                    loading={loading}
                    saveCopy={saveCopy}
                />
            </Page>
        );
    }

    private onEditLocalizationStream = (name: string) => {
        const { document } = this.props;

        const stream = (document.telemetry?.streams || [])
            .filter(_ => _.configuration.type === "ros-localization")
            .find(_ => _.name === name);

        if (stream) {
            this.activeLocalizationStream = stream;
            this.configuringMinimap = true;
        }
    };

    private onDeleteLocalizationStream = (name: string) => {
        const { document, onChange } = this.props;
        const telemetry = document.telemetry || {};

        const isMatchedStream = (it: IDeviceStreamConfiguration) =>
            it.name === name && it.configuration.type === "ros-localization";

        const previousStreams = telemetry.streams ?? [];
        const streams = previousStreams.filter(it => !isMatchedStream(it));

        onChange({
            ...document,
            telemetry: {
                ...telemetry,
                streams,
            },
        });

        this.activeLocalizationStream = undefined;
        this.configuringMinimap = false;
    };

    private onAddNewMinimapStart = () => {
        this.activeLocalizationStream = {
            name: "",
            configuration: {
                type: "ros-localization",
                baseReferenceFrame: "",
                odomTopic: "",
                rosVersion: defaultROSVersion, // TODO: [AN] Verify it is correct to use defaultROSVersion here (ROS 1)
            },
        };

        this.configuringMinimap = true;
    };

    private onCloseMinimap = () => {
        this.configuringMinimap = false;
    };

    private onOpenNumericConfig = (name: string, _: string) => () => {
        this.editingNumeric = name;
    };

    private onCloseEditingNumericConfig = () => {
        this.editingNumeric = undefined;
    };

    private onAddMinimap = (
        stream: IDeviceStreamConfiguration,
        replacesStream?: IDeviceStreamConfiguration,
    ) => {
        const { document, onChange } = this.props;
        const telemetry = document.telemetry || {};

        const isMatchedStream = (it: IDeviceStreamConfiguration) =>
            (it.name === stream.name || it.name === replacesStream?.name) &&
            it.configuration.type === "ros-localization";

        const streams = (telemetry.streams || [])
            .slice()
            .filter(it => !isMatchedStream(it));

        streams.push(stream);

        onChange({
            ...document,
            telemetry: {
                ...telemetry,
                streams,
            },
        });

        this.activeLocalizationStream = undefined;
        this.configuringMinimap = false;
    };

    private onSaveJoystickConfig = (
        joysticks?: ITeleopJoystickConfiguration[],
    ) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};

        onChange({
            ...document,
            teleop: {
                ...teleop,
                joysticks: joysticks || [],
            },
        });
    };

    private onOpenJoystickConfig = (name: string, _: string) => () => {
        this.editingJoystick = true;
        this.editingJoystickVelocityStreamWithName = name;
    };

    private onCloseJoystickConfig = () => {
        this.editingJoystick = false;
    };

    private onCloseEditingAudio = () => {
        this.editingAudio = undefined;
    };
    private onCloseEditingCompressedImage = () => {
        this.editingCompressedImage = undefined;
    };

    private onOpenButtonConfig = (name: string, _: string) => () => {
        this.editingButton = name;
    };

    private onCloseEditingButtonConfig = () => {
        this.editingButton = undefined;
    };

    private onOpenJointStateConfig = (name: string, _: string) => () => {
        this.editingJointState = name;
    };

    private onCloseEditingJointStateConfig = () => {
        this.editingJointState = undefined;
    };

    private onOpenMouseClickConfig = (name: string, _: string) => () => {
        this.editingMouseClick = name;
    };
    private onCloseEditingMouseClickConfig = () => {
        this.editingMouseClick = undefined;
    };

    private onCloseEditingHardwareVideoConfig = () => {
        this.editingHardwareVideo = undefined;
    };

    private onDeleteStream = (
        streamName: string,
        mode: TeleopMode,
        label?: string,
    ) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};
        const customStreams = document.teleop?.customStreams || [];
        const rosStreams = document.teleop?.rosStreams || [];
        const hardwareStreams = document.teleop?.hardwareStreams || [];
        const views = document.teleop?.views || [{}, {}, {}, {}, {}];

        let deleteStream = true;

        if (label) {
            // delete stream label
            const stream = customStreams.find(
                _ => _.name === streamName && _.mode === mode,
            );
            if (stream && stream.labels) {
                stream.labels = stream.labels.filter(_ => _ !== label);
                deleteStream = stream.labels.length === 0;
            }
        }

        if (deleteStream) {
            // can't use map, since views is a tuple
            views.forEach(
                (_, index) =>
                    (views[index] = _.streamName === streamName ? {} : _),
            );
        }

        onChange({
            ...document,
            teleop: {
                ...teleop,
                views,
                rosStreams: rosStreams.filter(
                    ({ topicName, mode: topicMode }) =>
                        topicName !== streamName || topicMode !== mode,
                ),
                customStreams: customStreams.filter(
                    _ =>
                        _.name !== streamName ||
                        _.mode !== mode ||
                        (_.name === streamName &&
                            _.mode === mode &&
                            _.labels !== undefined &&
                            _.labels?.length !== 0),
                ),
                hardwareStreams: hardwareStreams.filter(
                    _ => _.name !== streamName || _.mode !== mode,
                ),
            },
        });
    };

    private onAddRosStream = (stream: IDeviceTeleopRosStreamConfiguration) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};
        const streams = document.teleop?.rosStreams || [];
        const views = document.teleop?.views || [{}, {}, {}, {}, {}];
        const viewSlot = views.findIndex(_ => _.streamName === undefined);

        const supportedTypes = [
            "formant/H264VideoFrame",
            "sensor_msgs/CompressedImage",
        ];

        if (viewSlot > -1 && supportedTypes.some(_ => _ === stream.topicType)) {
            views[viewSlot] = { streamName: stream.topicName };
        }
        if (stream.topicType === "audio_common_msgs/AudioData") {
            stream.audioCodec = "raw";
        }

        onChange({
            ...document,
            teleop: {
                ...teleop,
                views,
                rosStreams: [...streams, stream],
            },
        });
    };

    private onAddCustomStream = (
        stream: IDeviceTeleopCustomStreamConfiguration,
    ) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};
        const streams = document.teleop?.customStreams || [];
        const views = document.teleop?.views || [{}, {}, {}, {}, {}];
        const viewSlot = views.findIndex(_ => _.streamName === undefined);

        const supportedTypes = [
            rtcCompressedImageStreamType,
            rtcH264VideoFrameStreamType,
        ];

        if (
            viewSlot > -1 &&
            supportedTypes.some(_ => _ === stream.rtcStreamType)
        ) {
            views[viewSlot] = { streamName: stream.name };
        }

        onChange({
            ...document,
            teleop: {
                ...teleop,
                views,
                customStreams: [
                    ...streams.filter(
                        _ => _.name !== stream.name || _.mode !== stream.mode,
                    ),
                    stream,
                ],
            },
        });
    };

    private onAddHardwareStream = (
        stream: IDeviceTeleopHardwareStreamConfiguration,
    ) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};
        const streams = document.teleop?.hardwareStreams || [];
        const views = document.teleop?.views || [{}, {}, {}, {}, {}];
        const viewSlot = views.findIndex(_ => _.streamName === undefined);

        const supportedTypes = [
            rtcCompressedImageStreamType,
            rtcH264VideoFrameStreamType,
        ];

        if (
            viewSlot > -1 &&
            supportedTypes.some(_ => _ === stream.rtcStreamType)
        ) {
            views[viewSlot] = { streamName: stream.name };
        }

        onChange({
            ...document,
            teleop: {
                ...teleop,
                views,
                hardwareStreams: [
                    ...streams.filter(
                        _ => _.name !== stream.name || _.mode !== stream.mode,
                    ),
                    stream,
                ],
            },
        });
    };
}
